import React, { useState, useEffect } from 'react';

import { Dialog, Card, Collapse } from '@mui/material';
import { images, icons, markers } from "../../helpers/constants";
import { Trans, useTranslation } from "react-i18next";
import Button from '../Buttons/Button';
import { useIsMobile } from '../../helpers/customHooks';
import SegmentedControl from '../SegmentedControl/SegmentedControl';
import SafetyZoneMap from '../Map/SafetyZoneMap';
import { downloadPDF } from '../../helpers/helperFunctions';
import toast from 'react-hot-toast';
import { DatePicker, DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs'; // Import dayjs
import 'dayjs/locale/sv';
import NewButton from '../Buttons/NewButton';


const DeviceModal = ({show, centrals, deleteData, removeShared, onSave, type, data, onClose}) => {
    const MOBILE = useIsMobile();
    const { t } = useTranslation();
    const [errorText, setErrorText] = useState("")
    const [devicename, setDevicename] = useState("")
    const [speedlimit, setSpeedlimit] = useState("")
    const [speedTypeValue, setSpeedTypeValue] = useState()
    const [sharingStatus, setSharingStatus] = useState(data?.sharedstatus.sharedwith ? data.sharedstatus.sharedwith : [])
    const [selectedIcon, setSelectedIcon] = useState()
    const [selectedColor, setSelectedColor] = useState()
    const [powersavingMode, setPowersavingMode] = useState(false)
    const [pendingPowersaving, setPendingPowersaving] = useState(false)
    const [wakeupSettings, setWakeupSettings] = useState()
    const [pendingWakeup, setPendingWakeup] = useState(false)
    const [pendingTemperature, setPendingTemperature] = useState(false)
    const [temperatureSettings, setTemperatureSettings] = useState()
    const [pendingHumidity, setPendingHumidity] = useState(false)
    const [selectedAlarmCentral, setSelectedAlarmCentral] = useState(data?.modelspecificinfo?.alarmcenter?.centerid ? data?.modelspecificinfo?.alarmcenter?.centerid : -1)
    const [alarmAdditional, setAlarmAdditional] = useState(data?.modelspecificinfo?.alarmcenter?.additionalinfo ? data?.modelspecificinfo?.alarmcenter?.additionalinfo : "")
    const [overrideDate, setOverrideDate] = useState(dayjs().add(1, 'day'))
    const [overridePending, setOverridePending] = useState(false)
    const [pendingEngineblock, setPendingEngineblock] = useState(false)
    const [engineBlock, setEngineBlock] = useState(false)
    const [overrideActive, setOverrideActive] = useState(false)
    const wakeArray = [
        {
            value: 7200, // 2 hours in seconds
            label: `2 ${t("settings.hours", {count : 2})}`
        },
        {
            value: 14400, // 4 hours in seconds
            label: `4 ${t("settings.hours", {count : 4})}`
        },
        {
            value: 28800, // 8 hours in seconds
            label: `8 ${t("settings.hours", {count : 8})}`
        },
        {
            value: 43200, // 12 hours in seconds
            label: `12 ${t("settings.hours", {count : 12})}`
        },
        {
            value: 86400, // 1 day in seconds
            label: `1 ${t("settings.days", {count : 1})}`
        },
        {
            value: 172800, // 2 days in seconds
            label: `2 ${t("settings.days", {count : 2})}`
        },
        {
            value: 345600, // 4 days in seconds
            label: `4 ${t("settings.days", {count : 4})}`
        },
        {
            value: 604800, // 7 days in seconds
            label: `7 ${t("settings.days", {count : 7})}`
        }
    ];

        const _alarmcenter = data?.alarmcenter
        const _dataplan = data?.dataplan
        const _exportdata = data?.exportdata
        const _iotinfo = data?.iotinfo
        const _sharedstatus = data?.sharedstatus
        const _speedlimit = data?.speedlimit
        const _stolenmode = data?.stolenmode
        const _visibleswitch = data?.visibleswitch
        const _external = data?.external_hardwareinfo

    useEffect(() => {
        if(!data && !type) return;


        setSelectedIcon(data?.mapicon?.split("_")[0] ? data?.mapicon?.split("_")[0] : "default")
        setSelectedColor(data?.mapicon?.split("_")[1] ? data?.mapicon?.split("_")[1] : "blue")
        setDevicename(data?.name)
        setSpeedlimit(_speedlimit?.info ?? 0)
        setSpeedTypeValue(_speedlimit?.unitraw ? _speedlimit?.unitraw : 1)
        setSharingStatus(_sharedstatus?.sharedwith ? _sharedstatus?.sharedwith : [])
        
        setPowersavingMode(_iotinfo?.psm?.pending ? _iotinfo?.psm?.pendingnewmode : _iotinfo?.psm?.info)
        setPendingPowersaving(_iotinfo?.psm?.pending)
        
        setPendingWakeup(_iotinfo?.wakeupinfo?.newsettings?.pending)
        setWakeupSettings(_iotinfo?.wakeupinfo?.newsettings.pending ? _iotinfo?.wakeupinfo?.newsettings : _iotinfo?.wakeupinfo?.currentsettings)
        setTemperatureSettings(_iotinfo?.temp_hum?.currentsettings)
        if(_external){
            setOverridePending(_external?.override?.pending?.active)
            setOverrideDate(_external?.override?.pending?.overrideuntil ? dayjs(_external?.override?.pending?.overrideuntil) : dayjs().add(1, 'day'))
            setPendingEngineblock(_external?.engineblock?.pending)
            setEngineBlock(_external?.engineblock?.pending ? _external?.engineblock?.newmode : _external?.engineblock?.status)
            setOverrideActive(_external?.override?.pending?.overrideuntil ? true : false)
        }
        
    }, [data, type])

    useEffect(() => {
        if(_iotinfo?.temp_hum?.currentsettings){
            const updatedSettings = Object.entries(_iotinfo?.temp_hum?.currentsettings).reduce((acc, [key, value]) => {
                // Replace null with 0, otherwise keep the original value
                if (key.includes('AlarmSwitch')) {
                    acc[key] = value === null ? false : value;
                } else {
                    acc[key] = value === null ? 0 : value;
                }
                return acc;
            }, {});
        
            // Check if the update is necessary to avoid infinite loop
            const isUpdateNeeded = Object.values(updatedSettings).some(value => value === 0);
            setTemperatureSettings(updatedSettings);

        }
    }, [_iotinfo?.temp_hum?.currentsettings]);

    

    useEffect(() => {
        if(pendingWakeup){
            setWakeupSettings(_iotinfo?.wakeupinfo?.newsettings)
        }else{
        }
    }, [pendingWakeup])

    const resetPending = () => {
        handleSave(type)
    }

    const resetOverride = () => {
        let data = {
            deviceid: [data?.id],
            override: "cancel"
        } 
        onSave(data, true) 
    }

    const mapIcons = [
        "default", "airplane", "ambulance", "bicycle", "boat", "bus", "car", "caravan", "container", "electriccar", "engine", "excavator", "forklift", "helicopter", "jet", "monstertruck", "moped", "motorcycle", "pickup", "rv", "sailboat", "snowmobile", "taxi", "tractor", "truck", "truckcontainer", "van"
    ]
    const colors = [
        "blue", "dark", "green", /* "grey",*/ "light", "orange", "yellow"
    ]



    
    const handleClose = () => {
        onClose()
      //  setShow(false)
    }

    const handleChange = (type, e) => {
        switch(type){
            case "devicename":
                setDevicename(e.target.value)
            break;
            case "speedlimit":
                setSpeedlimit(e.target.value)
            break;
            default: return
        }
        setErrorText("")
    }

    const handleSave = (type) => {
        let obj = {}
        let reset = false
        switch(type){
            case "devicename":
                obj = { deviceid: data?.id, name: devicename }
                break;
            case "speedlimit":
                obj = { deviceid: data?.id, speedlimit: parseInt(speedlimit), speedunit: speedTypeValue }
                break;
            case "map_icon":
                let icon = `${selectedIcon}_${selectedColor}`;
                obj = { deviceid: data?.id, mapicon: icon }
                break;
            case "powersaving":
                //reset = pendingPowersaving
                obj = { deviceid: data?.id, powersavingswitch: pendingPowersaving ? !powersavingMode : powersavingMode}
                break;
            case "wakeup":
                let current = _iotinfo?.wakeupinfo?.currentsettings
                reset = pendingWakeup
                obj = { 
                    deviceid: data?.id,
                    wakebyvibswitch: pendingWakeup ? current.wakebyvib ? true : false : wakeupSettings.wakebyvib ? true : false, 
                    wakebytimeswitch: pendingWakeup ? current.wakebytime ? true : false : wakeupSettings.wakebytime ? true : false,
                    wakebytimeinfo: pendingWakeup ? current.wakebytimeinfo : wakeupSettings.wakebytimeinfo,
                    wakebylightswitch: pendingWakeup ? current.wakebylight ? true : false : wakeupSettings.wakebylight ? true : false,
                    safetyzone: pendingWakeup ? current.safetyzone ? true : false : wakeupSettings.safetyzone ? true : false
                 }
                break;
            case "temperature":
                obj = { 
                    deviceid: data?.id,
                    temptype: temperatureSettings.tempType,
                    tempswitchexceed: temperatureSettings.tempExceedAlarmSwitch,
                    tempexceed: temperatureSettings.tempExceed,
                    tempswitchbelow:temperatureSettings.tempBelowAlarmSwitch,
                    tempbelow: temperatureSettings.tempBelow,
                }
                break;
            case "humidity":
                obj = { 
                    deviceid: data?.id,
                    humswitchexceed: temperatureSettings.humExceedAlarmSwitch,
                    humexceed: temperatureSettings.humExceed,
                    humswitchbelow: temperatureSettings.humBelowAlarmSwitch,
                    humbelow: temperatureSettings.humBelow,
                }
                break;
            case "alarm_central":
                reset = true
                obj = {
                    deviceid: data?.id,
                    alarmcenter: selectedAlarmCentral,
                }
                break;
            case "unlink_alarmcenter":
                reset = true
                obj = {
                    deviceid: data?.id,
                    unlink_alarmcenter: true,
                }
                setSelectedAlarmCentral(-1)
                break;
            case "alarm_central_update":
                obj = {
                    deviceid: data?.id,
                    alarmcenter_additionalinfo: alarmAdditional
                }
                break;
            case "override":
                obj = {
                    deviceid: [data?.id],
                    override: overrideDate.format("YYYY-MM-DD")
                }                
                break;
            case "engine_block":
                reset = pendingEngineblock
                obj = {
                    deviceid: [data?.id],
                    engineblock: pendingEngineblock ? !engineBlock : engineBlock
                }
                break;
            default: obj = {}
        }
        onSave(obj, reset)
    }

    const devicenameContent = () => {
        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        {/* <img src={icons.device_active} alt=""/> */}
                        <h3 className="ms-2">{t("settings.devicename")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <p className="grey-blue py-3">{t("settings.devicename_sub")}</p>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();// Prevent the default Enter key behavior
                        handleSave(type)
                    }}
                >
                    <div className="form-group d-flex align-items-center">
                        <input
                            type="text"
                            className="form-control rounded24 p-3 bg-light-grey grey-blue"
                            id="name"	
                            defaultValue={devicename}
                            onChange={(e) => {
                                handleChange("devicename", e);
                            }}
                        />
                        <div className="invalid-feedback ms-1 bottom">
                            {errorText === "" ? "" : errorText}
                        </div>
                    </div>
                </form>
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => handleSave(type)} className="bg-gradient rounded24 w-100 white-white p-3 ms-2 body bold">{t("settings.save")}</NewButton>
                </div>
            </div>
        )
    }

    const speedlimitContent = () => {
        /* 1 for km/h, 2 for mph, 3 for knots */
        const speedType = () => {
            const segments = [
                {
                    label: t("settings.kmh"),
                    value: 1
                },
                {
                    label: t("settings.mph"),
                    value: 2
                },
                {
                    label: t("settings.knots"),
                    value: 3
                }
            ]
            return (
                <SegmentedControl
                    name="psm"
                    callback={(val) => handleChangeSpeedType(val)}
                    value={speedTypeValue}
                    segments={segments}
                    className="bg-light-grey"
                />
            )
        }
    
        const handleChangeSpeedType = (value) => {
            setSpeedTypeValue(value)
        }


        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        {/* <img src={icons.tachometer} alt=""/> */}
                        <h3 className="">{t("settings.speedlimit")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <p className="grey-blue py-3">{t("settings.speedlimit_sub")}</p>
                <form>
                    <div className="form-group d-flex align-items-center">
                        <input
                            title='lol'
                            type="text"
                            className="form-control rounded24 p-3 bg-light-grey grey-blue"
                            id="speed"	
                            defaultValue={`${speedlimit}`}
                            onChange={(e) => {
                                handleChange("speedlimit", e);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();// Prevent the default Enter key behavior
                                    handleSave(type)
                                }
                            }}
                        />
                        <div className="invalid-feedback ms-1 bottom">
                            {errorText === "" ? "" : errorText}
                        </div>
                    </div>
                </form>
                <div className="mt-3">{speedType()}</div>
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => handleSave(type)} className="bg-gradient rounded24 w-100 white-white p-3 ms-2 body bold">{t("settings.save")}</NewButton>
                </div>
            </div>
        )
    }

    const mapiconContent = () => {
        return (
            <div className="">
                <div className="d-flex flex-row justify-content-between align-items-center p-3">
                    <div className='d-flex align-items-center'>
                        {/* <img src={icons.map_icon_title} alt=""/> */}
                        <h3 className="">{t("settings.map_icon")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <p className="grey-blue px-3">{t("settings.mapicon_sub")}</p>
                <div className="notification-scroll mt-3">
                    <div className="d-flex flex-row slider_x align-items-center align-self-center icon-container px-3">
                        {selectedIcon && mapIcons.map((icon, index) => (
                            <img key={index} onClick={() => setSelectedIcon(icon)} className={`size_45 cursor-pointer ${selectedIcon === icon && "scale-icon"}`} src={markers[selectedColor][icon]} alt=""/>
                        ))}
                    </div>
                    <div className="my-3 d-flex justify-content-between mx-5">
                        {selectedColor && colors.map((color, index) => (
                            <div key={index} onClick={() => setSelectedColor(color)} className={`d-flex justify-content-center align-items-center  cursor-pointer mapicon size_25 p-2 full-rounded ${color}`}>
                                <img src={icons.check} alt="" className={`${color === "light" ? "dark-grey-icon" : "white-icon"} size_12 ${color === selectedColor ? "": "d-none"}`}/>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between align-items-center mt-4 px-3 pb-3">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => handleSave(type)} className="bg-gradient rounded24 w-100 white-white p-3 ms-2 body bold">{t("settings.save")}</NewButton>
                </div>
            </div>
        )
    }

    const powersavingContent = () => {
        const psmBlocked = _iotinfo?.psm?.dataplan_locked ?? false
        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        {/* <img src={icons.battery_settings} alt=""/> */}
                        <h3 className="">{t("settings.powersaving_mode")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <p className="grey-blue py-3">{t("settings.powersaving_sub")}</p>
                {pendingPowersaving &&
                    <div className="d-flex justify-content-center align-items-center green cursor-pointer pb-3" onClick={() => resetPending()}>
                        <p className="gradient">{t("settings.reset_pending")}</p>
                        <img className="size_15 ms-2" src={icons.reset_arrow} alt="..." /> 
                    </div>
                }
                <div className={`d-flex justify-content-between bg-light-grey align-items-center p-3 rounded24`}>
                    <h4 className="regular">{t("settings.powersaving_mode")}</h4>
                    {psmBlocked
                        ?                    
                        <img className="cursor-pointer my-1" src={icons.info_icon} onClick={() => toast(t("toast.cant_change"), {icon: <img className="size_20" src={icons.info_icon} alt=""/>})} alt="..." />
                        :
                        <div className="form-check form-switch">
                            <input className="form-check-input" disabled={pendingPowersaving} onChange={() => setPowersavingMode(!powersavingMode)} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={powersavingMode}/>
                        </div>
                    }
                </div>
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => handleSave(type)} className={`bg-gradient rounded24 w-100 white-white ms-2 p-3 body bold ${pendingPowersaving || psmBlocked ? "disabled" : ""}`}>{t("settings.save")}</NewButton>
                </div>
            </div>
        )
    }


    const deleteHistoryContent = () => {
        let sharedByMe = data?.sharedstatus.sharedwith?.length > 0
        let sharedWithMe = data?.sharedstatus.withme
        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        <h3 className="">{t("settings.delete_data")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                {sharedByMe 
                    ? <p className="grey-blue pt-3">{t("settings.delete_data_body_sharedbyme")}</p>
                    : sharedWithMe
                    ? <p className="grey-blue pt-3">{t("settings.delete_data_body_sharedwithme")}</p>
                    : <p className="grey-blue pt-3">{t("settings.delete_data_body")}</p>
                }
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => deleteData(type)} className={`bg-red rounded24 w-100 white-white ms-2 p-3 body bold`}>{t("settings.delete")}</NewButton>
                </div>
            </div>
        )
    }

    const deleteDeviceContent = () => {
        let sharedByMe = data?.sharedstatus.sharedwith.length > 0
        let sharedWithMe = data?.sharedstatus.withme
        const active_plan = data?.dataplan?.dataplan_active && data?.dataplan?.dataplan_status !== "ending";
        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        <h3 className="">{t("settings.delete_device")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                {
                sharedWithMe
                    ? <p className="grey-blue pt-3">{t("settings.delete_device_body_sharedwithme")}</p>
                    : active_plan
                    ? <p className="grey-blue pt-3">{t("settings.delete_device_body_active_plan")}</p>
                    : sharedByMe
                    ? <p className="grey-blue pt-3">{t("settings.delete_device_body_sharedbyme")}</p>
                    : <p className="grey-blue pt-3">{t("settings.delete_device_body")}</p>
                    
                }
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => deleteData(type)} className={`bg-red rounded24 w-100 white-white ms-2 p-3 body bold ${active_plan ? "disabled": ""}`}>{t("settings.delete")}</NewButton>
                </div>
            </div>
        )
    }
    const sharingContent = () => {
        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        <h3 className="">{t("settings.sharing")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <div className="mt-3">
                    {sharingStatus.map((shared, index) => (
                        <div key={index} className={`d-flex flex-row justify-content-between py-2 align-items-center ${index !== sharingStatus.length - 1 ? "border-bottom": ""}`}>
                            <div className="d-flex flex-column">
                                <p className="grey-blue bold">{shared.email}</p>
                                <p className={`grey-blue ${shared.pending ? "" : "d-none"}`}>{t("settings.awaiting_confirmation")}</p>
                            </div>
                            <img className="cursor-pointer" src={icons.cross} onClick={() => removeShared(shared.pending, shared.userid, shared.email)} alt="..." />
                        </div>
                    ))}
                </div>
                {sharingStatus.length === 0 &&
                    <p className="grey-blue py-3">{t("settings.no_shared_accounts")}</p>
                }
                <div className="d-flex flex-row justify-content-end align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-50 dark-grey p-3 body bold">{t("settings.close")}</NewButton>
                </div>
            </div>
        )
    }

    const updateWakeup = (type) => {
        let currentIndex, newValueIndex;
        switch (type) {
            case "minus":
            case "plus":
                // Find the current index of wakebytimeinfo in the wakeArray
                currentIndex = wakeArray.findIndex(item => item.value === (wakeupSettings.wakebytimeinfo || 0));
                // Determine the new index based on the operation
                if (type === "plus") {
                    newValueIndex = currentIndex + 1 < wakeArray.length ? currentIndex + 1 : currentIndex; // Ensure it does not exceed array bounds
                } else if (type === "minus") {
                    newValueIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : currentIndex; // Ensure it does not go below 0
                }
                // Update the state only if a valid new index is found
                if (newValueIndex !== undefined && newValueIndex !== currentIndex) {
                    setWakeupSettings(prevState => ({
                        ...prevState,
                        wakebytimeinfo: wakeArray[newValueIndex].value
                    }));
                }
                break;
            case "wakebyvib":
            case "wakebytime":
            case "wakebylight":
                setWakeupSettings((prevState) => {
                    // Check if the setting to be toggled is the only one currently set to 1
                    const otherKeys = ['wakebyvib', 'wakebytime', 'wakebylight'].filter(key => key !== type);
                    const allOthersZero = otherKeys.every(key => (prevState[key] === 0 || !prevState[key]));
                    const currentValue = prevState[type];
    
                    if (allOthersZero && (currentValue === 1 || currentValue)) {
                        // Prevent toggling if it is the last remaining '1'
                        toast.error(t("toast.one_active"))
                        return prevState;
                    } else {
                        return {
                            ...prevState,
                            [type]: !currentValue
                        };
                    }
                });
                break;
            case "safetyzone":
                // Toggle the corresponding setting
                if(!_iotinfo?.position?.hasposition){
                    toast.error(t("toast.no_pos_safety_zone"))
                    return
                }
                setWakeupSettings((prevState) => ({
                    ...prevState,
                    [type]: !prevState[type]
                }));
                break;
            default:
                // Do nothing if the type is unrecognized
                break;
        }
    };

    const wakeupContent = () => {
        let waketimeLabel = wakeupSettings && (wakeArray.find(item => item.value === wakeupSettings.wakebytimeinfo)?.label || `${wakeupSettings.wakebytimeinfo}s`);
        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        {/* <img src={icons.battery_full} alt=""/> */}
                        <h3 className="">{t("settings.wakeup_mode")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <p className="grey-blue py-3">{t("settings.wakeup_mode_sub")}</p>
                {pendingWakeup &&
                    <div className="d-flex justify-content-center align-items-center gradient cursor-pointer pb-3" onClick={() => resetPending()}>
                        <p className="gradient">{t("settings.reset_pending_wakeup")}</p>
                        <img className="size_15 ms-2" src={icons.reset_arrow} alt="..." /> 
                    </div>
                }
                <div className={`p-3 d-flex justify-content-between bg-light-grey align-items-center rounded24 mb-3`}>
                    <h4 className={`regular ${pendingWakeup ? "dark_grey": ""}`}>{t("settings.vibration_wakeup")}</h4>
                    <div className="form-check form-switch">
                        <input className={`form-check-input`} disabled={pendingWakeup} onChange={() => updateWakeup("wakebyvib")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={wakeupSettings && wakeupSettings.wakebyvib}/>
                    </div>
                </div>
                <div className="p-3 d-flex flex-column bg-light-grey rounded24 mb-3">
                    <div className={`d-flex justify-content-between align-items-center`}>
                        <h4 className={`regular ${pendingWakeup ? "dark_grey": ""}`}>{t("settings.time_wakeup")}</h4>
                        <div className="form-check form-switch">
                            <input className={`form-check-input`} disabled={pendingWakeup} onChange={() => updateWakeup("wakebytime")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={wakeupSettings && wakeupSettings.wakebytime}/>
                        </div>
                    </div> 
                    <div className="d-flex justify-content-between align-items-center my-3">
                        <div className="bg-grey rounded12 d-flex cursor-pointer p-3">
                            <p className={`vertical-divider-dark pe-3 cursor-pointer ${pendingWakeup ? "dark_grey": ""}`} onClick={() => !pendingWakeup && wakeupSettings && wakeupSettings.wakebytime && updateWakeup("minus")}>-</p>
                            <p className={`ps-3 cursor-pointer ${pendingWakeup ? "dark_grey": ""}`} onClick={() => !pendingWakeup && wakeupSettings && wakeupSettings.wakebytime && updateWakeup("plus")}>+</p>
                        </div>
                        <p className={`p-3 bg-grey rounded12 ${pendingWakeup ? "dark_grey": ""}`}>{waketimeLabel}</p>
                    </div>
        
            
                    <div className={`d-flex justify-content-between align-items-center`}>
                        <h4 className={`regular ${pendingWakeup || ( wakeupSettings && wakeupSettings.wakebyvib) ? "grey-blue": "grey-blue"}`}>{t("settings.safety_zone")}</h4>
                        <div className="form-check form-switch">
                            <input className="form-check-input" disabled={pendingWakeup || ( wakeupSettings && wakeupSettings.wakebyvib)} onChange={() => updateWakeup("safetyzone")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={wakeupSettings && (wakeupSettings.wakebyvib ? false : wakeupSettings.safetyzone)}/>
                        </div>
                    </div>
                    {_iotinfo?.position?.hasposition &&
                        <Collapse in={wakeupSettings && wakeupSettings.safetyzone && !wakeupSettings.wakebyvib}>
                                <p className="grey-blue py-3">{t("settings.safety_zone_sub")}</p>
                                <div className="rounded24 safety-map-border" style={{width: "auto", height: "200px"}}>
                                    <SafetyZoneMap device={data}/>
                                </div>
                        </Collapse>   
                    }
                </div>
                <div className={`p-3 d-flex justify-content-between bg-light-grey align-items-center rounded24 mb-3`}>
                    <h4 className={`regular ${pendingWakeup ? "dark_grey": ""}`}>{t("settings.wakeby_light")}</h4>
                    <div className="form-check form-switch">
                        <input className="form-check-input" disabled={pendingWakeup} onChange={() => updateWakeup("wakebylight")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={wakeupSettings && wakeupSettings.wakebylight}/>
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => handleSave(type)} className={`bg-gradient rounded24 w-100 white-white p-3 ms-2 body bold ${pendingWakeup ? "disabled" : ""}`}>{t("settings.save")}</NewButton>
                </div>
            </div>
        )
    }

    const updateTemperature = (type) => {
        let data;
        switch(type){
            case "exceeds":
                data = "tempExceedAlarmSwitch";
                break;
            case "falls_below":
                data = "tempBelowAlarmSwitch";
                break;
            default: data = type
        }

        if(data === "plus_exceeds"){
            setTemperatureSettings((prevState) => ({
                ...prevState,
                tempExceed: prevState.tempExceed + 1
            }))
        }else if(data === "minus_exceeds"){
            setTemperatureSettings((prevState) => ({
                ...prevState,
                tempExceed: prevState.tempExceed - 1
            }))
        }else if(data === "plus_below"){
            setTemperatureSettings((prevState) => ({
                ...prevState,
                tempBelow: prevState.tempBelow + 1
            }))
        }else if(data === "minus_below"){
            setTemperatureSettings((prevState) => ({
                ...prevState,
                tempBelow: prevState.tempBelow - 1
            }))
        }else{
            setTemperatureSettings((prevState) => ({
                ...prevState,
                [data]: !prevState[data]
            }))
        }

    }

    const temperatureContent = () => {
        const temperatureType = () => {
            const segments = [
                {
                    label: t("settings.celsius"),
                    value: 0
                },
                {
                    label: t("settings.farenheit"),
                    value: 1
                }
            ]
            return (
                <SegmentedControl
                    name="psm"
                    callback={(val) => handleChangeTemperatureType(val)}
                    value={temperatureSettings && temperatureSettings.tempType}
                    segments={segments}
                    className="bg-light-grey"
                />
            )
        }
    
        const handleChangeTemperatureType = (value) => {
            setTemperatureSettings((prevState) => ({
                ...prevState,
                tempType: value 
            }))
        }


        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        {/* <img src={icons.battery_full} alt=""/> */}
                        <h3 className="">{t("settings.temperature_settings")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <p className="grey-blue py-3">{t("settings.temperature_sub")}</p>
                {pendingTemperature &&
                    <div className="d-flex justify-content-center align-items-center green cursor-pointer pb-3" onClick={() => alert("RESET")}>
                        <p className="gradient">{t("settings.reset_pending_temperature")}</p>
                        <img className="size_15 ms-2" src={icons.reset_arrow} alt="..." /> 
                    </div>
                }
                <div className="mb-3">{temperatureType()}</div>
                <div className="p-3 d-flex flex-column bg-light-grey rounded24 mb-3">
                    <div className={`d-flex justify-content-between align-items-center`}>
                        <h4 className="regular">{t("settings.exceeds")}</h4>
                        <div className="form-check form-switch">
                            <input className={`form-check-input`} onChange={() => updateTemperature("exceeds")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={temperatureSettings && temperatureSettings.tempExceedAlarmSwitch}/>
                        </div>
                    </div> 
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="bg-grey rounded12 d-flex cursor-pointer p-3">
                            <p className="vertical-divider-dark pe-3 cursor-pointer" onClick={() => temperatureSettings && temperatureSettings.tempExceed && updateTemperature("minus_exceeds")}>-</p>
                            <p className="ps-3 cursor-pointer" onClick={() => temperatureSettings && updateTemperature("plus_exceeds")}>+</p>
                        </div>
                        <p className="p-3 bg-grey rounded12">{temperatureSettings && temperatureSettings.tempExceed ? temperatureSettings.tempExceed : 0} °{temperatureSettings?.tempType === 0 ? "C" : "F"}</p>
                    </div>
                </div>
                <div className="p-3 d-flex flex-column bg-light-grey rounded24">
                    <div className={`d-flex justify-content-between align-items-center`}>
                        <h4 className="regular">{t("settings.falls_below")}</h4>
                        <div className="form-check form-switch">
                            <input className={`form-check-input`} onChange={() => updateTemperature("falls_below")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={temperatureSettings && temperatureSettings.tempBelowAlarmSwitch}/>
                        </div>
                    </div> 
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="bg-grey rounded12 d-flex cursor-pointer p-3">
                            <p className="vertical-divider-dark pe-3 cursor-pointer" onClick={() => temperatureSettings && updateTemperature("minus_below")}>-</p>
                            <p className="ps-3 cursor-pointer" onClick={() => temperatureSettings && updateTemperature("plus_below")}>+</p>
                        </div>
                        <p className="p-3 bg-grey rounded12">{temperatureSettings && temperatureSettings.tempBelow ? temperatureSettings.tempBelow : 0} °{temperatureSettings?.tempType === 0 ? "C" : "F"}</p>
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => handleSave(type)} className="bg-gradient rounded24 w-100 white-white p-3 ms-2 body bold">{t("settings.save")}</NewButton>
                </div>
            </div>
        )
    }

    const updateHumidity = (type) => {
        let data;
        switch(type){
            case "exceeds":
                data = "humExceedAlarmSwitch";
                break;
            case "falls_below":
                data = "humBelowAlarmSwitch";
                break;
            default: data = type
        }        
        if(data === "plus_exceeds"){
            setTemperatureSettings((prevState) => ({
                ...prevState,
                humExceed: prevState.humExceed < 90 ? prevState.humExceed + 5 : 95
            }))
        }else if(data === "minus_exceeds"){
            setTemperatureSettings((prevState) => ({
                ...prevState,
                humExceed: prevState.humExceed > 5 ? prevState.humExceed - 5 : 5
            }))
        }else if(data === "plus_below"){
            setTemperatureSettings((prevState) => ({
                ...prevState,
                humBelow: prevState.humBelow < 90 ? prevState.humBelow + 5 : 95
            }))
        }else if(data === "minus_below"){            
            setTemperatureSettings((prevState) => ({
                ...prevState,
                humBelow: prevState.humBelow > 5 ? prevState.humBelow - 5 : 5
            }))
        }else{
            setTemperatureSettings((prevState) => ({
                ...prevState,
                [data]: !prevState[data]
            }))
        }

    }



    const humidityContent = () => {
        return (
            <div className="p-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                    <div className='d-flex align-items-center'>
                        {/* <img src={icons.battery_full} alt=""/> */}
                        <h3 className="">{t("settings.humidity_settings")}</h3>
                    </div>
                    <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
                </div>
                <p className="grey-blue py-3">{t("settings.humidity_sub")}</p>
                {pendingHumidity &&
                    <div className="d-flex justify-content-center align-items-center green cursor-pointer pb-3" onClick={() => alert("RESET")}>
                        <p className="gradient">{t("settings.reset_pending_humidity")}</p>
                        <img className="size_15 ms-2" src={icons.reset_arrow} alt="..." /> 
                    </div>
                }
                <div className="p-3 d-flex flex-column bg-light-grey rounded24 mb-3">
                    <div className={`d-flex justify-content-between align-items-center`}>
                        <h4 className="regular">{t("settings.exceeds")}</h4>
                        <div className="form-check form-switch">
                            <input className={`form-check-input`} onChange={() => updateHumidity("exceeds")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={temperatureSettings && temperatureSettings.humExceedAlarmSwitch}/>
                        </div>
                    </div> 
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="bg-grey rounded12 d-flex cursor-pointer p-3">
                            <p className="vertical-divider-dark cursor-pointer pe-3" onClick={() => temperatureSettings && temperatureSettings.humExceed && updateHumidity("minus_exceeds")}>-</p>
                            <p className="ps-3 cursor-pointer" onClick={() => temperatureSettings && updateHumidity("plus_exceeds")}>+</p>
                        </div>
                        <p className="p-3 bg-grey rounded12">{temperatureSettings && temperatureSettings.humExceed ? temperatureSettings.humExceed : 0}%</p>
                    </div>
                </div>
                <div className="p-3 d-flex flex-column bg-light-grey rounded24">
                    <div className={`d-flex justify-content-between align-items-center`}>
                        <h4 className="regular">{t("settings.falls_below")}</h4>
                        <div className="form-check form-switch">
                            <input className={`form-check-input`} onChange={() => updateHumidity("falls_below")} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={temperatureSettings && temperatureSettings.humBelowAlarmSwitch}/>
                        </div>
                    </div> 
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="bg-grey rounded12 d-flex cursor-pointer p-3">
                            <p className="vertical-divider-dark cursor-pointer pe-3" onClick={() => temperatureSettings && temperatureSettings.humBelow && updateHumidity("minus_below")}>-</p>
                            <p className="cursor-pointer ps-3" onClick={() => temperatureSettings && temperatureSettings.humBelow && updateHumidity("plus_below")}>+</p>
                        </div>
                        <p className="p-3 bg-grey rounded12">{temperatureSettings && temperatureSettings.humBelow ? temperatureSettings.humBelow : 0}%</p>
                    </div>
                </div>
                <div className="d-flex flex-row justify-content-between align-items-center mt-4">
                    <NewButton onClick={() => onClose()} className="bg-grey rounded24 w-100 dark-grey me-2 p-3 body bold">{t("settings.cancel")}</NewButton>
                    <NewButton onClick={() => handleSave(type)} className="bg-gradient rounded24 w-100 white-white p-3 ms-2 body bold">{t("settings.save")}</NewButton>
                </div>
            </div>
        )
    }

    const alarmCentralContent = () => {
        return (
            <>
            <div className="d-flex flex-row p-3 justify-content-between border-settings align-items-center">
                <div className='d-flex align-items-center'>
                    <img src={icons.central} alt=""/>
                    <h3 className="ms-2">{t("settings.alarm_central")}</h3>
                </div>
                <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
            </div>
            <div className="p-3">
                <p className="thin dark-grey text-center pb-3">{t("settings.alarm_central_sub")}</p>
                <div className="d-flex justify-content-betwen">
                    <select onChange={(event) => setSelectedAlarmCentral(event.target.value)} value={selectedAlarmCentral} className="form-control bg-light-grey">
                        <option value="">{t("settings.select")}</option>
                        {centrals?.map((central, index) => (
                            <option key={index} value={central.id}>{central.name}</option>
                        ))}
                    </select>
                    {/* <Button onClick={() => downloadCentralData(selectedAlarmCentral)} icon={icons.download} notext={true} btnClass="btn-transparent bg-dark-grey ms-3" /> */}
                </div>
            </div>
            <div className="d-flex flex-row p-3 justify-content-end border-settings-top align-items-center">
               <Button onClick={() => onClose()} btnClass="btn-secondary me-2">{t("settings.cancel")}</Button>
               <Button onClick={() => handleSave(type)} btnClass="btn-primary">{t("settings.save")}</Button>
            </div>
            </>
        )
    }

    const alarmCentralContentSelected = () => {

        const unlinkCentral = () => {
            handleSave("unlink_alarmcenter")
        }

        const downloadCentralData = (value) => {
            const central = centrals?.find(item => parseInt(item.id) === parseInt(value));
            if (central && central.url) {
                downloadPDF(central.url)
            } else {
                toast.error(t("toast.error"))
            }        
        }

        return (
            <>
            <div className="d-flex flex-row p-3 justify-content-between border-settings align-items-center">
                <div className='d-flex align-items-center'>
                    <img src={icons.central} alt=""/>
                    <h3 className="ms-2">{t("settings.alarm_central")}</h3>
                </div>
                <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
            </div>
            <div className="p-3">
                <p className="thin dark-grey text-center pb-3">
                            <Trans
                            i18nKey="settings.alarm_central_sub_selected"
                            components={{
                            central: <span className="bold"/>,
                            }}
                            values={{
                            central: _alarmcenter?.centername,
                            }}
                        />
                </p>
                <p className="mb-3 text-center text-gradient underline cursor-pointer bold" onClick={() => downloadCentralData(selectedAlarmCentral)}>{t("settings.download_contract")}</p>
                <textarea rows={3} placeholder={t("settings.additional_info_placeholder")} defaultValue={alarmAdditional} onChange={(event) => setAlarmAdditional(event.target.value)} className="pb-3 form-control text-area-boy bg-light-grey" />    
                <p className="mt-3 text-center text-danger underline cursor-pointer bold" onClick={() => unlinkCentral()}>{t("settings.alarm_central_unlink")}</p>
                <p className="thin text-center">{t("settings.alarm_central_unlink_disclaimer")}</p>

            </div>
            <div className="d-flex flex-row p-3 justify-content-end border-settings-top align-items-center">
               <Button onClick={() => onClose()} btnClass="btn-secondary me-2">{t("settings.cancel")}</Button>
               <Button onClick={() => handleSave("alarm_central_update")} btnClass="btn-primary">{t("settings.save")}</Button>
            </div>
            </>
        )
    }

    const overrideContent = () => {
        return (
            <>
            <div className="d-flex flex-row p-3 justify-content-between border-settings align-items-center">
                <div className='d-flex align-items-center'>
                    <img src={icons.device_active} alt=""/>
                    <h3 className="ms-2">{t("settings.override")}</h3>
                </div>
                <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
            </div>
            <div className="p-3">
                {overridePending || pendingEngineblock || engineBlock
                    ? <p className="thin dark-grey text-center pb-3">{t("settings.clear_mode_override")}</p>
                    : <p className="thin dark-grey text-center pb-3">{t("devices.override_sub")}</p>
                }
                {overridePending &&
                    <div className="d-flex justify-content-center align-items-center green cursor-pointer pb-3" onClick={() => resetOverride()}>
                        <p className="gradient">{t("settings.reset_pending")}</p>
                        <img className="size_15 ms-2 primary-icon" src={icons.reset_arrow} alt="..." /> 
                    </div>
                }
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='sv'>
                    <DatePicker
                        disabled={overridePending || pendingEngineblock || engineBlock}
                        //ampm={false}
                        value={overrideDate}
                        onChange={(newValue) => setOverrideDate(newValue)}
                        minDate={dayjs()}
                        className="date_label w-100 disabled"
                    />
                </LocalizationProvider>
            </div>
            <div className="d-flex flex-row p-3 justify-content-end border-settings-top align-items-center">
               <Button onClick={() => onClose()} btnClass="btn-secondary me-2">{t("settings.cancel")}</Button>
               <Button onClick={() => handleSave(type)} btnClass="btn-primary">{t("settings.save")}</Button>
            </div>
            </>
        )
    }

    const engineblockContent = () => {
        return (
            <>
            <div className="d-flex flex-row p-3 justify-content-between border-settings align-items-center">
                <div className='d-flex align-items-center'>
                    <img src={icons.battery_settings} alt=""/>
                    <h3 className="ms-2">{t("settings.engine_block")}</h3>
                </div>
                <img className="cursor-pointer" src={icons.close_gray} onClick={() => onClose()} alt="..." /> 
            </div>
            <div className="p-3">
                    {overridePending || pendingEngineblock || overrideActive
                        ? <p className="thin dark-grey text-center pb-3">{t("settings.clear_mode_engineblock")}</p>
                        : <p className="thin dark-grey text-center pb-3">{t("settings.engine_block_sub")}</p>
                    }
                    
                    {pendingEngineblock &&
                        <div className="d-flex justify-content-center align-items-center green cursor-pointer pb-3" onClick={() => resetPending()}>
                            <p className="gradient">{t("settings.reset_pending")}</p>
                            <img className="size_15 ms-2 primary-icon" src={icons.reset_arrow} alt="..." /> 
                        </div>
                    }
                    <div className={`px-3 py-2 d-flex justify-content-between bg-light-grey align-items-center border-sm`}>
                        <h4 className="regular">{t("settings.engine_block")}</h4>
                        <div className="form-check form-switch">
                            <input className="form-check-input" disabled={overridePending || pendingEngineblock} onChange={() => setEngineBlock(!engineBlock)} type="checkbox" role="switch" id="flexSwitchCheckDefault" checked={engineBlock}/>
                        </div>
                    </div> 
            </div>
            <div className="d-flex flex-row p-3 justify-content-end border-settings-top align-items-center">
               <Button onClick={() => onClose()} btnClass="btn-secondary me-2">{t("settings.cancel")}</Button>
               <Button onClick={() => handleSave(type)} btnClass={`btn-primary ${pendingEngineblock ? "disabled" : ""}`}>{t("settings.save")}</Button>
            </div>
            </>
        )
    }

    const typeRender = () => {
        switch(type){
            case "devicename": return devicenameContent()
            case "speedlimit": return speedlimitContent()
            case "delete_history": return deleteHistoryContent()
            case "delete_device": return deleteDeviceContent()
            case "sharing": return sharingContent()
            case "map_icon": return mapiconContent()
            case "powersaving": return powersavingContent()
            case "wakeup": return wakeupContent()
            case "temperature": return temperatureContent()
            case "humidity": return humidityContent()
            case "alarm_central": return _alarmcenter?.centerid === null ? alarmCentralContent() : alarmCentralContentSelected()
            case "override": return overrideContent()
            case "engine_block": return engineblockContent()
            default: return null
        }
    }
    
    return(
        <>
        <Dialog fullWidth={MOBILE ? true : false} open={show} onClose={handleClose}>
            <Card className="settings-dialog d-flex flex-column scrollable-y">
                <div className="w-100 h-100 grey-blue">
                    {typeRender()}
                </div>
            </Card>
        </Dialog>
        </>
    )
}

export default DeviceModal;